import { useReactiveVar } from '@apollo/client'
import AndroidIcon from '@mui/icons-material/Android'
import AppleIcon from '@mui/icons-material/Apple'
import MenuIcon from '@mui/icons-material/Menu'
import { styled } from '@mui/material'
import Button from '@mui/material/Button'
import Hidden from '@mui/material/Hidden'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import { MouseEvent, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { selectModeEnabledVar } from '../../providers/apollo/cache'
import useSelectMedia from '../../views/account/views/gallery/hooks/useSelectMedia'
import AlbumSlideshow from '../../views/account/views/gallery/components/iconButtons/AlbumSlideshow'
import MediaDetails from '../../views/account/views/gallery/components/iconButtons/MediaDetails'
import MediaDownload from '../../views/account/views/gallery/components/iconButtons/MediaDownload'
import MediaPrintShop from '../../views/account/views/gallery/components/iconButtons/MediaPrintShop'

interface UnauthenticatedMenuProps {
  anchorEl: null | HTMLElement
  handleClose(): void
}

const AndroidButton = styled(IconButton)(() => ({
  background: 'none',
  borderBottom: '1px solid #ffffff',
  borderLeft: 'none',
  borderRight: 'none',
  borderTop: '1px solid #ffffff',
  borderRadius: 0,
  color: '#ffffff',
  margin: 0,
  padding: '.3rem 1rem .35rem 1rem',
  '&:hover': {
    backgroundColor: '#2b2b2b',
    color: '#ffffff'
  }
}))

const AppleButton = styled(IconButton)(() => ({
  background: 'none',
  borderBottom: '1px solid #ffffff',
  borderLeft: 'none',
  borderRight: '1px solid #ffffff',
  borderTop: '1px solid #ffffff',
  borderRadius: 0,
  color: '#ffffff',
  margin: 0,
  padding: '.3rem 1rem .35rem 1rem',
  '&:hover': {
    backgroundColor: '#2b2b2b'
  }
}))

const SignUpButton = styled(Button)(() => ({
  background: 'rgba(255, 255, 255, .75)',
  textTransform: 'uppercase',
  fontWeight: 500,
  fontFamily: '"Montserrat", Open Sans',
  border: '1px solid #ffffff',
  borderRadius: '0 1.5rem 1.5rem 0',
  color: '#2b2b2b',
  fontSize: '.875rem',
  margin: 0,
  maxHeight: '2.25rem',
  padding: '.6rem 1rem .55rem 1rem',
  '&:hover': {
    backgroundColor: '#002D62',
    color: '#ffffff'
  }
})) as typeof Button

const SignInButton = styled(Button)(() => ({
  background: 'none',
  textTransform: 'uppercase',
  fontFamily: '"Montserrat", Open Sans',
  fontWeight: 500,
  border: '1px solid #ffffff',
  borderRadius: '1.5rem 0 0 1.5rem',
  color: '#ffffff',
  fontSize: '.875rem',
  margin: 0,
  maxHeight: '2.25rem',
  padding: '.6rem 1rem .55rem 1.25rem',
  '&:hover': {
    backgroundColor: '#002D62'
  }
})) as typeof Button

const openAndroidAppDownload = () => {
  window.open(
    'https://play.google.com/store/apps/details?id=com.photobucket.android&utm_medium=web&utm_source=navBar&utm_content=deviceIcons&utm_campaign=v2_android',
    '_blank'
  )
}

const openiOSAppDownload = () => {
  window.open(
    'https://apps.apple.com/us/app/photobucket-backup/id314439840?utm_medium=web&utm_source=navBar&utm_content=deviceIcons&utm_campaign=v2_apple',
    '_blank'
  )
}

function UnauthenticatedDropDown(props: UnauthenticatedMenuProps) {
  return (
    <Menu anchorEl={props.anchorEl} open={Boolean(props.anchorEl)} onClose={props.handleClose}>
      <MenuItem component={Link} to="/signup" onClick={props.handleClose} data-test="appbar-dropdown-signup" id="appbar-dropdown-signup">
        Get Started
      </MenuItem>
      <MenuItem
        onClick={() => {
          openiOSAppDownload()
          props.handleClose()
        }}
        data-test="appbar-dropdown-ios"
        id="appbar-dropdown-ios"
      >
        Download iOS
      </MenuItem>
      <MenuItem
        onClick={() => {
          openAndroidAppDownload()
          props.handleClose()
        }}
        data-test="appbar-dropdown-android"
        id="appbar-dropdown-android"
      >
        Download Android
      </MenuItem>
    </Menu>
  )
}

export default function UnauthenticatedMenu() {
  const { isEmpty } = useSelectMedia()
  const { selectedMedia } = useSelectMedia()
  const selectModeEnabled = useReactiveVar(selectModeEnabledVar)
  const selectMediaModeIsOn = selectModeEnabled.media || selectModeEnabled.albums || !isEmpty()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { userId, albumId } = useParams<{ userId?: string; albumId?: string }>()

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <div style={{ flex: 1, textAlign: 'right' }}>
      {userId &&
        (selectMediaModeIsOn ? (
          <>
            <MediaDetails ids={selectedMedia} isPublic />
            <MediaDownload ids={selectedMedia} isUnauthenticated isPublic />
            <AlbumSlideshow albumId={albumId} username={userId} disabled={!albumId} />
            <MediaPrintShop ids={selectedMedia} isDisabled={false} />
          </>
        ) : (
          <AlbumSlideshow albumId={albumId} username={userId} disabled={false} />
        ))}
      <Hidden mdDown>
        {/* signin btn */}
        <SignInButton color="inherit" component={Link} to="/login" data-test="appbar-signin" id="appbar-signin">
          Sign In
        </SignInButton>

        {/* iOS btn */}
        <Tooltip
          arrow
          title="Download iOS"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#2b2b2b',
                borderRadius: '1.5rem',
                fontSize: '.85rem',
                lineHeight: 1.8,
                maxWidth: '16rem',
                padding: '1rem'
              }
            }
          }}
        >
          <AppleButton id="appbar-mobile-app-dl-apple" onClick={openiOSAppDownload}>
            <AppleIcon sx={{ fontSize: '1.4575rem', maxHeight: '1.5rem' }} />
          </AppleButton>
        </Tooltip>

        {/* google btn */}
        <Tooltip
          arrow
          title="Download Android"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#2b2b2b',
                borderRadius: '1.5rem',
                fontSize: '.85rem',
                lineHeight: 1.8,
                maxWidth: '16rem',
                padding: '1rem'
              }
            }
          }}
        >
          <AndroidButton id="appbar-mobile-app-dl-android" onClick={openAndroidAppDownload}>
            <AndroidIcon sx={{ fontSize: '1.4575rem', maxHeight: '1.5rem' }} />
          </AndroidButton>
        </Tooltip>

        {/* get started btn */}
        <SignUpButton color="inherit" data-test="appbar-signup" id="appbar-signup" variant="outlined" component={Link} to="/signup">
          Get Started
        </SignUpButton>
      </Hidden>

      <Hidden mdUp>
        <Button
          sx={{ color: '#ffffff', fontSize: '.85rem' }}
          color="inherit"
          component={Link}
          data-test="appbar-signin"
          id="appbar-signin"
          to="/login"
        >
          Sign In
        </Button>
        <IconButton
          color="inherit"
          sx={{
            '& svg': {
              width: 30,
              height: 30
            }
          }}
          data-test="appbar-dropdown-toggle"
          id="appbar-dropdown-toggle"
          onClick={handleClick}
          size="large"
        >
          <Tooltip
            id="appbar-mobile-hamburger"
            arrow
            title="Get Started"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#2b2b2b',
                  borderRadius: '1.5rem',
                  fontSize: '.85rem',
                  lineHeight: 1.8,
                  maxWidth: '16rem',
                  padding: '1rem'
                }
              }
            }}
          >
            <MenuIcon />
          </Tooltip>
        </IconButton>
        <UnauthenticatedDropDown handleClose={handleClose} anchorEl={anchorEl} />
      </Hidden>
    </div>
  )
}
